import { useState, useRef, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Clock, Calendar, Timer, MapPin, WifiOff } from "lucide-react";
import AlertDialogConfirm from "@/components/AlertDialogConfirm";
import { useToast } from "@/hooks/use-toast";
import { ficharService } from "@/services/ficharService";
import SliderAction from "@/components/SliderAction";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import logo from "@/assets/logo.png";
import logoDark from "@/assets/logo-dark.png";
import { addToQueue, removeFromQueue, getQueue, getQueueCount } from "@/lib/offlineQueue";

import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import { QrReader } from 'react-qr-reader';
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

const defaultIcon = L.icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

interface ButtonAction {
  label: string;
  color: string;
  action: string;
  qr?: string;
  report?: string;
  location?: string;
  limitrange?: number;
  lat?: number;
  lng?: number;
  isItinerant?: boolean; // Nueva propiedad
}

interface FichajeProps {
  userData: any;
}

const Fichaje = ({ userData }: FichajeProps) => {
  const { toast } = useToast();
  const [lastAction, setLastAction] = useState(null);
  const [totalSecondsToday, setTotalSecondsToday] = useState(null);
  const [numberSecondsWorked, setNumberSecondsWorked] = useState(null);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [qrData, setQrData] = useState<string | null>(null);
  const [titleAlert, setTitleAlert] = useState("");
  const [textAlert, setTextAlert] = useState("");
  const [hashEmployee, setHashEmployee] = useState<any>(null);
  const [userId, setUserId] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [registro, setRegistro] = useState<any>(null);
  const [resetSlider, setResetSlider] = useState(false);
  const [currentDate, setCurrentDate] = useState("");
  const [currentTime, setCurrentTime] = useState("");
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [selectedBtn, setSelectedBtn] = useState<ButtonAction | null>(null);
  const [userPosition, setUserPosition] = useState<any>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportText, setReportText] = useState("");
  // GPS permission dialog
  const [showGpsDialog, setShowGpsDialog] = useState(false);
  const [pendingGpsBtn, setPendingGpsBtn] = useState<ButtonAction | null>(null);
  // Offline queue
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingQueue, setPendingQueue] = useState<import("@/lib/offlineQueue").QueuedFichaje[]>([]);
  const [showPendingList, setShowPendingList] = useState(false);

  const actionList = { I: "Entrada", O: "Salida", P: "Pausa", R: "Reanudación" };
  const [actionButtons, setActionButtons] = useState<ButtonAction[]>([]);
  const [loading, setLoading] = useState(true);

  const mapRef = useRef<L.Map | null>(null);
  const userMarkerRef = useRef<L.Marker | null>(null);

  useEffect(() => {
    if (numberSecondsWorked === null) return;
    const interval = setInterval(() => {
      if(lastAction == "I" || lastAction == "R"){
        setNumberSecondsWorked(prev => prev + 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [numberSecondsWorked, lastAction]);

  const destroyMap = () => {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      userMarkerRef.current = null;
    }
  };

  const actualizarFechaHora = () => {
    const ahora = new Date();
    setCurrentDate(ahora.toLocaleDateString("es-ES", { weekday: "long", year: "numeric", month: "long", day: "numeric" }));
    setCurrentTime(ahora.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" }));
  };

  useEffect(() => {
    actualizarFechaHora();
    const interval = setInterval(actualizarFechaHora, 60000);
    return () => clearInterval(interval);
  }, []);

  const refreshPendingState = () => {
    const q = getQueue();
    setPendingCount(q.length);
    setPendingQueue(q);
  };

  // Sincroniza fichajes guardados offline cuando se recupera la conexión
  const syncOfflineQueue = useCallback(async () => {
    const queue = getQueue();
    if (queue.length === 0) return;
    let synced = 0;
    for (const item of queue) {
      try {
        await ficharService.inserRecord(item.data);
        removeFromQueue(item.queueId);
        synced++;
      } catch {
        break; // Para en el primer fallo para mantener el orden
      }
    }
    refreshPendingState();
    if (synced > 0) {
      toast({ title: "Sincronizado", description: `${synced} fichaje${synced > 1 ? 's' : ''} enviado${synced > 1 ? 's' : ''} correctamente.` });
      cargarBotones();
    }
  }, []);

  useEffect(() => {
    refreshPendingState();
    const handleOnline = () => {
      toast({ title: "Conexión recuperada", description: "Sincronizando fichajes pendientes..." });
      syncOfflineQueue();
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [syncOfflineQueue]);

  useEffect(() => {
    const load = async () => {
      try {
        const dataEmployee: any = await ficharService.getDataEmployee(userData.id);
        setHashEmployee(dataEmployee.response.hash);
        setUserId(dataEmployee.response.ui)
        cargarBotones();
        setLoading(false);
      } catch (e) {
        console.error("Error en carga inicial", e);
      }
    };
    load();
  }, [userData.id]);

  const cargarBotones = async () => {
    try {
      const response: any = await ficharService.getLastAction({ id: userData.id, environment: "id" });
      if (!response?.response) return;

      let r = response.response;
      setLastAction(r.action);
      setTotalSecondsToday(r.total_seconds_today);
      setNumberSecondsWorked(r.number_seconds_worked ? r.number_seconds_worked : null);
      
      if (!r.action) r.action = "O";

      const isItinerant = r.local_id === "0";
      let requiereQrStart = r.startqr === "Y" ? "Y" : "N";
      let requiereQrOut = r.endqr === "Y" ? "Y" : "N";
      let requiereReport = r.reporte === "S" ? "Y" : "N";
      let requiereLocation = "N";
      let limitrange = r.limitrange ? parseInt(r.limitrange) : 50;
      let lat = r.lat ? parseFloat(r.lat) : 0;
      let lng = r.lng ? parseFloat(r.lng) : 0;

      let buttons: ButtonAction[] = [];

      switch (r.action) {
        case "I":
          if (r.endlocation === "Y") requiereLocation = "Y";
          buttons = [
            { label: "Salida", color: "danger", action: "O", qr: requiereQrOut, report: requiereReport, location: requiereLocation, limitrange, lat, lng, isItinerant },
            { label: "Pausa", color: "warning", action: "P", isItinerant },
          ];
          break;
        case "O":
          if (r.startlocation === "Y") requiereLocation = "Y";
          buttons = [
            { label: "Entrada", color: "success", action: "I", qr: requiereQrStart, location: requiereLocation, limitrange, lat, lng, isItinerant },
          ];
          break;
        case "P":
          buttons = [{ label: "Reanudación", color: "success", action: "R", isItinerant }];
          break;
        case "R":
          if (r.endlocation === "Y") requiereLocation = "Y";
          buttons = [
            { label: "Salida", color: "danger", action: "O", qr: "N", report: requiereReport, location: requiereLocation, limitrange, lat, lng, isItinerant },
            { label: "Pausa", color: "warning", action: "P", isItinerant },
          ];
          break;
      }
      setActionButtons(buttons);
    } catch (e) {
      console.error("Error en botones", e);
    }
  };

  const calcularDistancia = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371e3;
    const rad = (x: number) => (x * Math.PI) / 180;
    const dLat = rad(lat2 - lat1);
    const dLon = rad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  const formatDistance = (dist: number) => {
    if (dist >= 1000) {
      const km = Math.floor(dist / 1000);
      const meters = Math.round(dist % 1000);
      return `${km.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")} km${meters > 0 ? ` y ${meters} m.` : '.'}`;
    }
    return `${Math.round(dist)} m.`;
  };

  const handleSliderComplete = async (btn: ButtonAction) => {
    // LÓGICA ITINERANTE (SIN MODAL DE MAPA)
    if (btn.isItinerant) {
      setLoading(true);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setLoading(false);
          enviarConfirmacion(btn, { lat: pos.coords.latitude, lng: pos.coords.longitude });
        },
        (err) => {
          setLoading(false);
          if (err.code === err.PERMISSION_DENIED) {
            setPendingGpsBtn(btn);
            setShowGpsDialog(true);
          } else {
            toast({ title: "Error GPS", description: "No se pudo obtener la ubicación. Intenta de nuevo.", variant: "destructive" });
          }
          setResetSlider(true);
          setTimeout(() => setResetSlider(false), 100);
        },
        { enableHighAccuracy: true }
      );
      return;
    }

    // LÓGICA NORMAL CON GEOCERCA
    if (btn.location === "Y") {
      setSelectedBtn(btn);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const userLat = pos.coords.latitude;
          const userLng = pos.coords.longitude;
          setUserPosition({ lat: userLat, lng: userLng });
          setDistance(calcularDistancia(userLat, userLng, btn.lat!, btn.lng!));
          setShowLocationDialog(true);
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            setPendingGpsBtn(btn);
            setShowGpsDialog(true);
          } else {
            toast({ title: "Error GPS", description: "No se pudo obtener la ubicación. Intenta de nuevo.", variant: "destructive" });
          }
          setResetSlider(true);
          setTimeout(() => setResetSlider(false), 100);
        }
      );
      return;
    }

    if (btn.qr === "Y") {
      setLoading(true);
      try {
        await ficharService.generateQr({ "ui": userId, "ei": hashEmployee });
        setSelectedBtn(btn);
        setShowQrDialog(true);
        setLoading(false);
      } catch (e) {
        setLoading(false);
        toast({ title: "Error", description: "No se pudo generar el QR.", variant: "destructive" });
      }
      return;
    }

    enviarConfirmacion(btn);
  };

  const enviarConfirmacion = (btn: ButtonAction, itinerantPos?: {lat: number, lng: number}) => {
    if (btn.action == "O" && btn.report === "Y") {
      if(!reportText.trim()){
        setSelectedBtn(btn);
        setShowReportDialog(true);
        return;
      }
    }

    const dataRegistro = {
      id: hashEmployee,
      action: btn.action,
      reporte: reportText || undefined,
      latitude: itinerantPos?.lat || userPosition?.lat,
      longitude: itinerantPos?.lng || userPosition?.lng
    };

    setRegistro(dataRegistro);
    setTitleAlert("Confirmar Fichaje");
    setTextAlert(`¿Confirma enviar una ${actionList[btn.action]}? ${btn.isItinerant ? '(Modo Itinerante)' : ''}`);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    destroyMap();
    setLoading(true);

    // Sin conexión: guardar en cola local
    if (!navigator.onLine) {
      addToQueue(registro);
      refreshPendingState();
      toast({
        title: "Sin conexión",
        description: `Fichaje guardado. Se enviará automáticamente cuando recuperes internet.`,
      });
      cargarBotones();
      setActionButtons([]);
      setRegistro(null);
      setReportText("");
      setLoading(false);
      return;
    }

    try {
      await ficharService.inserRecord(registro);
      toast({ title: "¡Listo!", description: `Tu ${actionList[registro.action].toLowerCase()} se ha registrado correctamente.` });
      cargarBotones();
      setActionButtons([]);
      setRegistro(null);
      setReportText("");
      // Aprovecha para intentar sincronizar la cola offline si hay pendientes
      await syncOfflineQueue();
    } catch (e: any) {
      // Extraemos el mensaje real del error
      const errorMessage = e.message || "No se pudo registrar el fichaje.";
      
      toast({ 
        title: "Error de Sistema", 
        description: errorMessage, 
        variant: "destructive" 
      });

      // Opcional: Si quieres manejar el error 07 de forma especial (ej. no borrar la cola offline)
      console.error("Error en fichaje:", errorMessage);
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setShowConfirm(false);
    destroyMap();
    setResetSlider(true);
    setTimeout(() => setResetSlider(false), 100);
  };

  const handleReportConfirm = () => {
    if (!reportText.trim()) {
      toast({ title: "Error", description: "Debe completar el reporte del día.", variant: "destructive" });
      return;
    }
    enviarConfirmacion(selectedBtn!);
    setShowReportDialog(false);
  };

  const handleReportCancel = () => {
    setShowReportDialog(false);
    setResetSlider(true);
    setTimeout(() => setResetSlider(false), 100);
  };

  const handleRequestGpsPermission = async () => {
    setShowGpsDialog(false);
    // Nativo (Capacitor): solicita el permiso GPS al sistema
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const status = await Geolocation.requestPermissions();
      // Si el usuario concedió el permiso, reintenta automáticamente la acción
      if (status.location === 'granted' || status.coarseLocation === 'granted') {
        if (pendingGpsBtn) {
          const btn = pendingGpsBtn;
          setPendingGpsBtn(null);
          handleSliderComplete(btn);
        }
      }
    } catch {
      // Fallback web: no hay API para re-solicitar permiso una vez denegado,
      // la próxima llamada a getCurrentPosition mostrará el diálogo del navegador
      if (pendingGpsBtn) {
        const btn = pendingGpsBtn;
        setPendingGpsBtn(null);
        handleSliderComplete(btn);
      }
    }
  };

  useEffect(() => {
    if (showLocationDialog && selectedBtn) {
      if (!mapRef.current) {
        mapRef.current = L.map("map-location").setView([userPosition.lat!, userPosition.lng!], 18);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 22, attribution: "©️ FicharBot" }).addTo(mapRef.current);
        L.circle([selectedBtn.lat!, selectedBtn.lng!], { radius: selectedBtn.limitrange!, color: "green", weight: 2 }).addTo(mapRef.current);
      }
      if (!userMarkerRef.current && userPosition) {
        userMarkerRef.current = L.marker([userPosition.lat, userPosition.lng], { icon: defaultIcon }).addTo(mapRef.current);
      }
    }
  }, [showLocationDialog, userPosition, selectedBtn]);

  useEffect(() => {
    if (!showLocationDialog) return;
    const interval = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const userLat = pos.coords.latitude;
          const userLng = pos.coords.longitude;
          setUserPosition({ lat: userLat, lng: userLng });
          if (userMarkerRef.current) userMarkerRef.current.setLatLng([userLat, userLng]);
          if (selectedBtn) setDistance(calcularDistancia(userLat, userLng, selectedBtn.lat!, selectedBtn.lng!));
        },
        null,
        { enableHighAccuracy: true }
      );
    }, 10000);
    return () => clearInterval(interval);
  }, [showLocationDialog, selectedBtn]);
  
  const formatSeconds = (sec) => {
    const h = Math.floor(sec / 3600).toString().padStart(2, "0");
    const m = Math.floor((sec % 3600) / 60).toString().padStart(2, "0");
    const s = Math.floor(sec % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  if(loading){
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-white dark:bg-gray-900 z-50">
        <img src={logo}     alt="Logo" className="h-20 w-auto mb-6 dark:hidden" />
        <img src={logoDark} alt="Logo" className="h-20 w-auto mb-6 hidden dark:block" />
        <div className="w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-500 text-sm">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3 px-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl xs:text-2xl">Fichaje</CardTitle>
              <CardDescription className="text-sm xs:text-base mt-0.5">Gestiona tu jornada laboral</CardDescription>
            </div>
            <Clock className="w-7 h-7 xs:w-8 xs:h-8 text-primary shrink-0" />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-4 space-y-2">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="w-3.5 h-3.5 shrink-0" />
              <span className="text-xs xs:text-sm capitalize truncate">{currentDate}</span>
            </div>
            <div className="text-3xl xs:text-4xl font-bold text-foreground">{currentTime}</div>
            {totalSecondsToday && numberSecondsWorked && (
              <>
                <p className="text-sm capitalize text-right">
                  Tiempo trabajado <b>{formatSeconds(numberSecondsWorked)}</b>
                  <Timer className="w-6 h-6 mt-[-2px] text-primary float-right" />
                </p>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div className="h-3 bg-blue-500 rounded-full animate-pulse" style={{ width: `${Math.min((numberSecondsWorked / totalSecondsToday) * 100, 100)}%` }}></div>
                </div>
              </>
            )}
          </div>
          {pendingCount > 0 && (
            <div className="rounded-lg border border-yellow-200 dark:border-yellow-700 overflow-hidden">
              <button
                onClick={() => setShowPendingList(v => !v)}
                className="w-full flex items-center justify-between gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 text-left"
              >
                <div className="flex items-center gap-2">
                  <WifiOff className="w-4 h-4 text-yellow-600 dark:text-yellow-400 shrink-0" />
                  <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">
                    {pendingCount} fichaje{pendingCount > 1 ? 's' : ''} pendiente{pendingCount > 1 ? 's' : ''} por sincronizar
                  </span>
                </div>
                <span className="text-xs text-yellow-600 dark:text-yellow-400">
                  {showPendingList ? '▲ Ocultar' : '▼ Ver'}
                </span>
              </button>

              {showPendingList && (
                <div className="divide-y divide-yellow-100 dark:divide-yellow-800 bg-white dark:bg-gray-800">
                  {pendingQueue.map((item) => (
                    <div key={item.queueId} className="flex items-center justify-between px-4 py-2">
                      <div>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full mr-2 ${
                          item.data.action === 'I' ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300' :
                          item.data.action === 'O' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' :
                          item.data.action === 'P' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300' :
                          'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                        }`}>
                          {actionList[item.data.action] ?? item.data.action}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(item.timestamp).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {actionButtons.map((btn) => (
            <SliderAction key={btn.action} actionButton={btn} onComplete={handleSliderComplete} reset={resetSlider} />
          ))}
        </CardContent>
      </Card>

      <AlertDialogConfirm open={showConfirm} title={titleAlert} message={textAlert} onConfirm={handleConfirm} onCancel={handleCancel} />

      {showReportDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-end xs:items-center justify-center z-50 p-3 xs:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-4">
            <h2 className="text-lg xs:text-xl font-bold text-center dark:text-white">Reporte obligatorio</h2>
            <textarea className="w-full p-2 border rounded-lg mt-3 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm xs:text-base" rows={4} value={reportText} onChange={(e) => setReportText(e.target.value)} placeholder="Escriba aquí el reporte..." />
            <div className="mt-3 flex gap-3">
              <button onClick={handleReportCancel} className="w-1/2 py-2.5 border rounded-lg dark:border-gray-600 dark:text-gray-200 text-sm xs:text-base">Cancelar</button>
              <button onClick={handleReportConfirm} className="w-1/2 py-2.5 bg-primary text-white rounded-lg text-sm xs:text-base">Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {showQrDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-end xs:items-center justify-center z-50 p-3 xs:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg p-4">
            <h2 className="text-lg xs:text-xl font-bold text-center dark:text-white">Escanear QR</h2>
            <div className="mt-3">
              {!qrData ? (
                <QrReader constraints={{ facingMode: "environment" }} onResult={(result) => {
                  if (!!result) {
                    try {
                      let sData = result.getText();
                      sData = decodeURIComponent(sData).split("").reverse().join("");
                      sData = atob(sData).split("").reverse().join("");
                      sData = atob(sData);
                      let aData = JSON.parse(sData);
                      if (aData && aData.id === hashEmployee) setQrData("QR Validado correctamente.");
                    } catch(e) { console.error(e) }
                  }
                }} />
              ) : (
                <div className="py-8 text-center bg-secondary text-white rounded-lg">{qrData}</div>
              )}
            </div>
            <div className="mt-3 flex gap-3">
              <button onClick={() => { setShowQrDialog(false); setResetSlider(true); setTimeout(()=>setResetSlider(false),100); }} className="w-1/2 py-2.5 border rounded-lg dark:border-gray-600 dark:text-gray-200 text-sm xs:text-base">Cancelar</button>
              <button disabled={!qrData} onClick={() => { setShowQrDialog(false); enviarConfirmacion(selectedBtn!); }} className={`w-1/2 py-2.5 rounded-lg text-white text-sm xs:text-base ${!qrData ? "bg-gray-400" : "bg-primary"}`}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {showLocationDialog && selectedBtn && (
        <div className="fixed inset-0 bg-black/60 flex items-end xs:items-center justify-center z-50 p-3 xs:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg overflow-hidden">
            <div className="p-3 xs:p-4 border-b text-center">
              <h2 className="text-lg xs:text-xl font-bold dark:text-white">Confirmar ubicación</h2>
              <p className="text-xs xs:text-sm text-muted-foreground">Acércate al punto para habilitar el fichaje.</p>
            </div>
            <div className="relative h-52 xs:h-64"><div id="map-location" className="absolute inset-0" /></div>
            <div className="p-3 text-center">
              {distance !== null && <p className="text-sm dark:text-gray-300">Distancia: <strong>{formatDistance(distance)}</strong></p>}
            </div>
            <div className="p-3 xs:p-4 flex gap-3">
              <button onClick={() => { setShowLocationDialog(false); handleCancel(); }} className="w-1/2 py-2.5 border rounded-lg dark:border-gray-600 dark:text-gray-200 text-sm xs:text-base">Cancelar</button>
              <button disabled={distance! > selectedBtn.limitrange!} onClick={() => { setShowLocationDialog(false); enviarConfirmacion(selectedBtn); }} className={`w-1/2 py-2.5 rounded-lg text-white text-sm xs:text-base ${distance! > selectedBtn.limitrange! ? "bg-gray-400" : "bg-primary"}`}>Confirmar</button>
            </div>
          </div>
        </div>
      )}

      {showGpsDialog && (
        <div className="fixed inset-0 bg-black/60 flex items-end xs:items-center justify-center z-50 p-3 xs:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm p-4 xs:p-6 text-center space-y-3 xs:space-y-4">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <MapPin className="w-8 h-8 text-orange-500" />
              </div>
            </div>
            <h2 className="text-xl font-bold dark:text-white">Permiso de ubicación</h2>
            <p className="text-sm text-muted-foreground">
              Esta acción necesita acceso a tu GPS. Pulsa <strong>Activar GPS</strong> para solicitar el permiso al sistema. Si ya lo denegaste permanentemente, actívalo en <strong>Ajustes &gt; Ubicación</strong>.
            </p>
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => { setShowGpsDialog(false); setPendingGpsBtn(null); }}
                className="w-1/2 py-2 border rounded-lg dark:border-gray-600 dark:text-gray-200"
              >
                Cancelar
              </button>
              <button
                onClick={handleRequestGpsPermission}
                className="w-1/2 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <MapPin className="w-4 h-4" />
                Activar GPS
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Fichaje;